import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ErrorHandlingService } from '../../core/services/error-handling.service';
import {
  ApiRespons,
  MoveUserModel,
  OrgUserQueryParams,
  UserApiResponsePaginated,
  UserModel,
  searchObj,
  TitleQueryParams,
  Title,
  TitleModel,
} from './manage-organization.interface';
import { BehaviorSubject, Observable, tap, catchError, map } from 'rxjs';
import { ApiResponse } from '../organizations/organization.interface';
import {
  UploadEvent,
  UploadProgressService,
} from '../../core/services/upload-progress.service';
import { LookupItem } from '../../core/models/lookup.models';
import { BaseLineManagementPayload } from '../manage-settings/manage-settings.interface';
import { AuditFieldChange } from '../audit-logs/audit-logs.interface';
import { AuditLogsService } from '../audit-logs/audit-logs.service';
import {
  AuditModule,
  AuditOperationType,
} from '../audit-logs/audit-logs.constants';

@Injectable({
  providedIn: 'root',
})
export class ManageOrganizationService {
  private readonly uploadProgressService = inject(UploadProgressService);
  private readonly API_URL = `${environment.apiUrl}`;
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly auditLogsService = inject(AuditLogsService);
  private readonly organizationsTitleSubject =
    new BehaviorSubject<ApiRespons<Title> | null>(null);
  titles$ = this.organizationsTitleSubject.asObservable();
  private readonly organizationsUserSubject =
    new BehaviorSubject<UserApiResponsePaginated | null>(null);
  organizationUsers$ = this.organizationsUserSubject.asObservable();

  private readonly superAdminUsersSubject =
    new BehaviorSubject<UserApiResponsePaginated | null>(null);
  superAdminUsers$ = this.superAdminUsersSubject.asObservable();

  searchSubject = new BehaviorSubject<searchObj>({
    searchTerm: '',
    tabIndex: 0,
  });
  search$ = this.searchSubject.asObservable();

  totalRows = 0;

  private currentQueryParams: OrgUserQueryParams | null = null;

  getTitles(queryParams: TitleQueryParams): Observable<ApiRespons<Title>> {
    this.organizationsTitleSubject.next(null);
    const params = this.buildQueryParams(queryParams);

    return this.http
      .get<
        ApiRespons<Title>
      >(`${this.API_URL}/OrganizationTitle/get-by-org-id`, { params })
      .pipe(
        tap((response: ApiRespons<Title>) =>
          this.organizationsTitleSubject.next(response)
        ),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  private buildQueryParams({
    organizationId,
    sortOrder,
  }: TitleQueryParams): HttpParams {
    let params = new HttpParams();

    if (sortOrder !== undefined) {
      params = params.set('sortOrder', sortOrder.toString());
    }

    if (organizationId) {
      params = params.set('organizationId', organizationId.toString());
    }

    return params;
  }

  createTitle(payload: TitleModel): Observable<ApiRespons<Title>> {
    return this.http
      .post<
        ApiRespons<Title>
      >(`${this.API_URL}/OrganizationTitle/create`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updateTitle(
    titleId: number,
    paylod: TitleModel
  ): Observable<ApiRespons<TitleModel>> {
    const params = new HttpParams().set('titleId', titleId.toString());

    return this.http
      .put<
        ApiRespons<TitleModel>
      >(`${this.API_URL}/OrganizationTitle/update`, paylod, { params })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteTitle(id: number): Observable<ApiRespons<void>> {
    return this.http
      .delete<ApiRespons<void>>(`${this.API_URL}/OrganizationTitle/${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  mapRolesToTitle(
    payload: BaseLineManagementPayload
  ): Observable<ApiRespons<void>> {
    return this.http
      .post<
        ApiRespons<void>
      >(`${this.API_URL}/OrganizationTitle/map-roles-to-titles`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getMappedRolesToTitle(titleId: number): Observable<ApiRespons<number>> {
    return this.http
      .get<
        ApiRespons<number>
      >(`${this.API_URL}/OrganizationTitle/organization-title/roles?titleId=${titleId}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  private buildUserQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    searchTerm,
    organizationId,
    StatusId,
    isSuperAdmin,
  }: OrgUserQueryParams): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }

    if (sort) {
      params = params.set('sort', sort);
    }

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    if (organizationId) {
      params = params.set('organizationId', organizationId.toString());
    }

    if (StatusId) {
      params = params.set('statusId', StatusId.toString());
    }

    if (isSuperAdmin !== undefined) {
      params = params.set('isSuperAdmin', isSuperAdmin.toString());
    }

    return params;
  }

  clearOrgUsers(): void {
    this.organizationsUserSubject.next(null);
    this.currentQueryParams = null;
  }

  getOrgUsers(
    queryParams: OrgUserQueryParams
  ): Observable<UserApiResponsePaginated> {
    this.organizationsUserSubject.next(null);
    this.currentQueryParams = queryParams;
    const params = this.buildUserQueryParams(queryParams);
    return this.http
      .get<UserApiResponsePaginated>(`${this.API_URL}/User`, { params })
      .pipe(
        tap(response => {
          this.totalRows = response.result.totalRows;
          this.organizationsUserSubject.next(response);
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getSuperAdminUsers(
    queryParams: OrgUserQueryParams
  ): Observable<UserApiResponsePaginated> {
    this.superAdminUsersSubject.next(null);
    const params = this.buildUserQueryParams({
      ...queryParams,
      isSuperAdmin: true,
    });
    return this.http
      .get<UserApiResponsePaginated>(`${this.API_URL}/User`, { params })
      .pipe(
        tap(response => {
          this.superAdminUsersSubject.next(response);
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getUsersLookup(
    queryParams: OrgUserQueryParams
  ): Observable<UserApiResponsePaginated> {
    this.organizationsUserSubject.next(null);
    this.currentQueryParams = queryParams;
    const params = this.buildUserQueryParams(queryParams);
    return this.http
      .get<UserApiResponsePaginated>(`${this.API_URL}/user/get-users-lookup`, {
        params,
      })
      .pipe(
        tap(response => {
          this.totalRows = response.result.totalRows;
          this.organizationsUserSubject.next(response);
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getAllUsers(params: {
    organizationId?: number;
    pageSize: number;
    page?: number;
  }) {
    return this.http
      .get<UserApiResponsePaginated>(`${this.API_URL}/User`, { params })
      .pipe(
        tap(response => {
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getAllUsersExport(params: { organizationId: number; pageSize: number }) {
    params.pageSize = this.totalRows;
    return this.http
      .get<UserApiResponsePaginated>(`${this.API_URL}/User`, { params })
      .pipe(
        tap(response => {
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getOrgUserById(userId: number, orgId: number) {
    const url = orgId
      ? `${this.API_URL}/User/${userId}?organizationId=${orgId}`
      : `${this.API_URL}/User/${userId}`;
    return this.http.get<ApiResponse<UserModel>>(url).pipe(
      map((response: ApiResponse<UserModel>) => response),
      catchError(error => this.errorHandler.handleError(error))
    );
  }

  private refreshUser(payload: UserModel, pageIndex?: number): void {
    if (this.currentQueryParams) {
      const queryParams = { ...this.currentQueryParams };
      if (pageIndex) {
        queryParams.pageIndex = pageIndex;
      }

      this.getOrgUsers(queryParams).subscribe();
    } else {
      const orgID = payload.isParentOrg
        ? payload.isParentOrg
        : payload.organizationId;
      this.getOrgUsers({
        pageIndex: pageIndex ?? 1,
        pageSize: 10,
        organizationId: payload.isAllUsers ? undefined : orgID,
      }).subscribe();
    }
  }

  createOrgUser(
    payload: UserModel,
    pageIndex?: number
  ): Observable<ApiResponse<UserModel>> {
    return this.http
      .post<ApiResponse<UserModel>>(`${this.API_URL}/user/create`, payload)
      .pipe(
        tap(() => {
          this.refreshUser(payload, pageIndex);
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  editOrgUser(
    payload: UserModel,
    pageIndex?: number
  ): Observable<ApiResponse<UserModel>> {
    return this.http
      .put<
        ApiResponse<UserModel>
      >(`${this.API_URL}/user/update?userId=${payload.id}`, payload)
      .pipe(
        tap(() => {
          this.refreshUser(payload, pageIndex);
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  createSuperAdminUser(
    payload: UserModel,
    queryParams: OrgUserQueryParams
  ): Observable<ApiResponse<UserModel>> {
    return this.http
      .post<ApiResponse<UserModel>>(`${this.API_URL}/user/create`, payload)
      .pipe(
        tap(() => {
          this.getSuperAdminUsers(queryParams).subscribe();
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  editSuperAdminUser(
    payload: UserModel,
    queryParams: OrgUserQueryParams
  ): Observable<ApiResponse<UserModel>> {
    return this.http
      .put<
        ApiResponse<UserModel>
      >(`${this.API_URL}/user/update?userId=${payload.id}`, payload)
      .pipe(
        tap(() => {
          this.getSuperAdminUsers(queryParams).subscribe();
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  deleteOrgUser(userId: number, orgId: number): Observable<ApiRespons<void>> {
    return this.http
      .put<
        ApiRespons<void>
      >(`${this.API_URL}/user/delete-soft?userId=${userId}&organizationId=${orgId}`, {})
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  moveOrgUser(payload: MoveUserModel): Observable<ApiResponse<MoveUserModel>> {
    return this.http
      .put<
        ApiResponse<MoveUserModel>
      >(`${this.API_URL}/user/move-user`, payload)
      .pipe(
        tap(() => {
          if (this.currentQueryParams) {
            this.getOrgUsers({
              ...this.currentQueryParams,
            }).subscribe();
          } else {
            this.getOrgUsers({
              pageIndex: 1,
              pageSize: 10,
              organizationId: payload.currentOrganizationId,
            }).subscribe();
          }
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  bulkCreateUsers(
    file: File,
    parentOrganizationId: number,
    roleIds: number[]
  ): Observable<UploadEvent<void>> {
    const formData = new FormData();
    formData.append('file', file);

    let params = new HttpParams().set('orgId', parentOrganizationId.toString());

    roleIds.forEach(roleId => {
      params = params.append('roleId', roleId.toString());
    });

    return this.http
      .post<
        ApiResponse<void>
      >(`${this.API_URL}/User/add-bulk-user`, formData, { params, reportProgress: true, observe: 'events' })
      .pipe(
        this.uploadProgressService.getUploadProgress<void>(),
        tap(response => {
          if (response.status === 'completed') {
            if (this.currentQueryParams) {
              this.getOrgUsers({
                ...this.currentQueryParams,
                organizationId: parentOrganizationId,
              }).subscribe();
            } else {
              this.getOrgUsers({
                pageIndex: 1,
                pageSize: 10,
                organizationId: parentOrganizationId,
              }).subscribe();
            }
          }
        })
      );
  }

  resendEmailToUser(
    userIds: number[],
    orgId: number | null,
    modifiedFields?: AuditFieldChange[]
  ): Observable<ApiResponse<void>> {
    let params = new HttpParams().append('orgId', orgId?.toString() || '');
    let payload = {};
    userIds.forEach(id => {
      params = params.append('userIds', id.toString());
    });
    if (modifiedFields && modifiedFields.length > 0) {
      payload = {
        _modifiedFields: modifiedFields,
      };
    }

    return this.http
      .post<
        ApiResponse<void>
      >(`${this.API_URL}/user/send-an-email-to-user`, payload, { params })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getAllowedOrganizationsToMoveUser(
    orgId: number | null,
    searchTerm: string,
    excludeCurrentOrganization = true
  ): Observable<ApiResponse<LookupItem[]>> {
    let params = new HttpParams();
    if (searchTerm) {
      params = params.set('searchTerm', this.sanitizeSearchTerm(searchTerm));
    }
    params = params.set(
      'excludeCurrentOrganization',
      excludeCurrentOrganization.toString()
    );
    return this.http
      .get<
        ApiResponse<LookupItem[]>
      >(`${this.API_URL}/User/get-allowed-organizations-to-move-user/${orgId}`, { params })
      .pipe(
        map(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  private sanitizeSearchTerm(searchTerm: string): string {
    return searchTerm ? searchTerm.trim() : '';
  }

  logUserExportAudit(organizationId: number): Observable<unknown> {
    return this.auditLogsService.logAudit({
      auditModule: AuditModule.UserManagement,
      auditOperationType: AuditOperationType.Exported,
      summary: 'Users exported',
      organizationIdOverride: organizationId === 0 ? null : organizationId,
      entityType: 'UserMangmentExport',
    });
  }

  logUserExportFailureAudit(organizationId: number): Observable<unknown> {
    return this.auditLogsService.logAudit({
      auditModule: AuditModule.UserManagement,
      auditOperationType: AuditOperationType.Exported,
      summary: 'Users failed to export',
      organizationIdOverride: organizationId === 0 ? null : organizationId,
      entityType: 'UserMangmentExport',
    });
  }
}
